/**
 * cart-rewards.js
 * Tiered shipping & free gift threshold bar for Shopify cart drawer.
 *
 * SETUP REQUIRED:
 *   1. Add {% render 'cart-rewards' %} inside your cart drawer section liquid file.
 *   2. Include this script + cart-rewards.css in your theme.liquid or cart section.
 *
 * DYNAMIC DATA (managed automatically via cart-rewards.liquid):
 *   • Excluded variants   — products tagged "Has Always Charge Shipping" whose
 *                           variants have metafield custom.always_incurs_shipping_charge = true
 *   • Tier 2 gift options — all in-stock variants of products tagged "Free Gift Tier2"
 *   • Tier 3 gift options — all in-stock variants of products tagged "Free Gift Tier3"
 *
 * FREE GIFT PRICING ($0):
 *   Items are added with a `_is_free_gift: true` line property to flag them.
 *   To zero out the price at checkout, use a Shopify Automatic Discount (Buy X Get Y,
 *   100% off) or Shopify Functions / Scripts (Shopify Plus).
 */

(function () {
  'use strict';

  // ──────────────────────────────────────────────────────────────
  // CONFIGURATION — only thresholds live here; everything else
  // comes from the Liquid snippet at render time.
  // ──────────────────────────────────────────────────────────────

  const TIERS = [
    { threshold: 5000,  type: 'shipping'    }, // $50  – free shipping
    { threshold: 7500,  type: 'gift_tier2'  }, // $75  – + Tier 2 gift
    { threshold: 10000, type: 'gift_tier3'  }, // $100 – + Tier 3 gift
  ];

  // ──────────────────────────────────────────────────────────────
  // HELPERS
  // ──────────────────────────────────────────────────────────────

  function formatMoney(cents) {
    const dollars = Math.ceil(cents / 100);
    return `$${dollars}`;
  }

  function fetchJSON(url, options = {}) {
    return fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      ...options,
    }).then(r => r.json());
  }

  // ──────────────────────────────────────────────────────────────
  // CART REWARDS CUSTOM ELEMENT
  // ──────────────────────────────────────────────────────────────

  class CartRewards extends HTMLElement {
    constructor() {
      super();
      this._cart       = null;
      this._config     = null;
      this._busy       = false;
      this._onUpdate   = this._handleExternalUpdate.bind(this);
    }

    // ── Lifecycle ───────────────────────────────────────────────

    connectedCallback() {
      this._config = this._loadConfig(); // ← back to synchronous, works fine now
      document.addEventListener('product:added-to-cart', this._onUpdate);
      document.addEventListener('cart:rewards:refresh',  this._onUpdate);
      const modal = document.querySelector('cart-modal');
      if (modal) modal.addEventListener('show', this._onUpdate);
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('product:added-to-cart', this._onUpdate);
      document.removeEventListener('cart:rewards:refresh',  this._onUpdate);
      const modal = document.querySelector('cart-modal');
      if (modal) modal.removeEventListener('show', this._onUpdate);
    }

    // ── Public API ───────────────────────────────────────────────

    async refresh() {
      try {
        // NOTE: _config is intentionally NOT reloaded here. It is parsed once
        // in connectedCallback() while the Liquid JSON block is still present
        // in the DOM. Calling _loadConfig() again after _render() has run would
        // find no JSON block and return empty arrays, wiping out the gift options.
        // When the cart drawer section is fully reloaded (reloadContent), the
        // element is recreated and connectedCallback fires again with fresh data.
        this._cart = await this._fetchCart();
        await this._enforceGiftEligibility();
        this._render();
      } catch (err) {
        console.error('[CartRewards] refresh error:', err);
      }
    }

    // ── Private: cart API ────────────────────────────────────────

    async _fetchCart() {
      return fetchJSON('/cart.js');
    }

    /**
     * Reads the JSON config block written by cart-rewards.liquid at render time.
     * Returns safe defaults if the block is missing or unparseable.
     */
    _loadConfig() {
      const el = document.querySelector('script[data-cart-rewards-config]'); // ← was this.querySelector
      const defaults = { excludedVariantIds: [], giftProducts: { tier2: [], tier3: [] } };
      if (!el) return defaults;
      try {
        const parsed = JSON.parse(el.textContent);
        // Merge so missing keys always fall back to empty arrays
        return {
          excludedVariantIds: parsed.excludedVariantIds || [],
          giftProducts: {
            tier2: (parsed.giftProducts && parsed.giftProducts.tier2) || [],
            tier3: (parsed.giftProducts && parsed.giftProducts.tier3) || [],
          },
        };
      } catch (e) {
        console.error('[CartRewards] failed to parse config JSON:', e);
        return defaults;
      }
    }

    /**
     * Returns subtotal in cents, excluding:
     *   • free gift line items (added by this component)
     *   • variants flagged by the "Has Always Charge Shipping" tag +
     *     custom.always_incurs_shipping_charge metafield (via Liquid config)
     */
    _qualifyingSubtotal() {
      if (!this._cart || !this._config) return 0;

      const excludedSet = new Set(this._config.excludedVariantIds.map(String));

      const deductTotal = this._cart.items
        .filter(item =>
          this._isGiftLine(item) ||
          excludedSet.has(String(item.variant_id))
        )
        .reduce((sum, item) => sum + item.final_line_price, 0);

      return this._cart.total_price - deductTotal;
    }

    _isGiftLine(item) {
      return item.properties && item.properties._is_free_gift === 'true';
    }

    _currentTier() {
      const sub = this._qualifyingSubtotal();
      if (sub >= TIERS[2].threshold) return 3;
      if (sub >= TIERS[1].threshold) return 2;
      if (sub >= TIERS[0].threshold) return 1;
      return 0;
    }

    _progressPercent() {
      const sub = Math.min(this._qualifyingSubtotal(), TIERS[2].threshold);
      return (sub / TIERS[2].threshold) * 100;
    }

    /** Returns which gift variants are currently in the cart, keyed by tier. */
    _giftsInCart() {
      const result = { tier2: null, tier3: null };
      if (!this._cart || !this._config) return result;

      const t2ids = new Set(this._config.giftProducts.tier2.map(p => String(p.variantId)));
      const t3ids = new Set(this._config.giftProducts.tier3.map(p => String(p.variantId)));

      for (const item of this._cart.items) {
        if (!this._isGiftLine(item)) continue;
        const vid = String(item.variant_id);
        if (t2ids.has(vid)) result.tier2 = { variantId: vid, key: item.key };
        if (t3ids.has(vid)) result.tier3 = { variantId: vid, key: item.key };
      }
      return result;
    }

    /** Remove gifts that no longer qualify due to cart subtotal dropping. */
    async _enforceGiftEligibility() {
      if (!this._cart || this._busy) return;

      const tier   = this._currentTier();
      const gifts  = this._giftsInCart();
      const remove = [];

      if (tier < 2 && gifts.tier2) remove.push(gifts.tier2.key);
      if (tier < 3 && gifts.tier3) remove.push(gifts.tier3.key);

      if (remove.length === 0) return;

      this._busy = true;
      try {
        await Promise.all(remove.map(key => this._removeLineItem(key)));
        this._cart = await this._fetchCart();
        // Notify the cart drawer to refresh its line items
        this._reloadCartDrawer();
      } finally {
        this._busy = false;
      }
    }

    async _removeLineItem(key) {
      await fetchJSON(window.routes.cart_change_url, {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
    }

    async _addGift(variantId, tier) {
      if (this._busy) return;
      this._busy = true;

      try {
        // Swap out existing gift of the same tier if it's a different variant
        const gifts = this._giftsInCart();
        const existing = gifts[`tier${tier}`];
        if (existing && existing.variantId !== variantId) {
          await this._removeLineItem(existing.key);
        }

        await fetchJSON(window.routes.cart_add_url, {
          method: 'POST',
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: {
              _is_free_gift: 'true',
              _gift_tier:    String(tier),
            },
          }),
        });

        this._cart = await this._fetchCart();
        this._reloadCartDrawer();
        this._render();
      } catch (err) {
        console.error('[CartRewards] addGift error:', err);
      } finally {
        this._busy = false;
      }
    }

    /** Trigger the theme's cart drawer to re-render its line items. */
    _reloadCartDrawer() {
      const modal = document.querySelector('cart-modal');
      if (modal && typeof modal.reloadContent === 'function') {
        modal.reloadContent();
      }
      // Update the cart bubble count
      const count = this._cart && this._cart.item_count;
      const bubble = document.getElementById('CartBubble');
      if (bubble) {
        if (count > 0) {
          bubble.classList.remove('hidden');
          bubble.textContent = count;
        } else {
          bubble.classList.add('hidden');
          bubble.textContent = '';
        }
      }
    }

    // ── Private: event handling ──────────────────────────────────

    _handleExternalUpdate() {
      // Small debounce to let Shopify's cart settle before we read it
      clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => this.refresh(), 400);
    }

    // ── Private: copy / messaging ────────────────────────────────

    _messageHTML(tier, subtotal) {
      const need = t => `<strong>${formatMoney(t - subtotal)}</strong>`;

      switch (tier) {
        case 0:
          return `Add ${need(TIERS[0].threshold)} more to unlock free shipping.`;
        case 1:
          return `You unlocked free shipping! Add ${need(TIERS[1].threshold)} more to get a free gift.`;
        case 2:
          return `You unlocked free shipping + a free gift! Add ${need(TIERS[2].threshold)} more to get another free gift.`;
        case 3:
          return `🎉 You unlocked free shipping and two free gifts!`;
        default:
          return '';
      }
    }

    _unlockedRewards(tier) {
      const all = [
        { tier: 1, icon: '🚚', label: 'Free Shipping'                                    },
        { tier: 2, icon: '🦴', label: 'Free Gift!'             },
        { tier: 3, icon: '🎾', label: 'Free Gift!'        },
      ];
      return all.filter(r => tier >= r.tier);
    }

    // ── Private: render ──────────────────────────────────────────

    _render() {
      if (!this._cart) { this.innerHTML = ''; return; }

      const subtotal  = this._qualifyingSubtotal();
      const tier      = this._currentTier();
      const progress  = this._progressPercent();
      const gifts     = this._giftsInCart();

      // ── Progress bar ──────────────────────────────────────────
      const milestonesHTML = TIERS.map((t, i) => {
        const pos     = (t.threshold / TIERS[2].threshold) * 100;
        const reached = subtotal >= t.threshold;
        const icons   = ['🚚', '🦴', '🎾'];
        return `
          <div class="cr-milestone ${reached ? 'cr-milestone--reached' : ''}"
               style="left:${pos}%"
               aria-label="${formatMoney(t.threshold)} – ${reached ? 'unlocked' : 'locked'}">
            <span class="cr-milestone__dot">${reached ? icons[i] : ''}</span>
            <span class="cr-milestone__label">${formatMoney(t.threshold)}</span>
          </div>`;
      }).join('');

      const progressHTML = `
        <div class="cr-progress" role="progressbar"
             aria-valuenow="${Math.round(progress)}" aria-valuemin="0" aria-valuemax="100"
             aria-label="Reward progress: ${Math.round(progress)}%">
          <div class="cr-progress__fill" style="width:${progress}%"></div>
          ${milestonesHTML}
        </div>`;

      // ── Dynamic message ───────────────────────────────────────
      const messageHTML = `
        <p class="cr-message">${this._messageHTML(tier, subtotal)}</p>
        <p class="cr-disclaimer">Excludes Petmate Indigo Dog Houses and 36&rdquo; kennels and above</p>`;

      // ── Unlocked badges ───────────────────────────────────────
      const rewards      = this._unlockedRewards(tier);
      const rewardsHTML  = rewards.length ? `
        <div class="cr-unlocked" aria-label="Unlocked rewards">
          ${rewards.map(r => `
            <span class="cr-badge">
              <span class="cr-badge__icon">${r.icon}</span>
              <span class="cr-badge__label">${r.label}</span>
            </span>`).join('')}
        </div>` : '';

      // ── Gift selectors ────────────────────────────────────────
      const selectorHTML = [
        tier >= 2 ? this._selectorHTML(2, this._config.giftProducts.tier2, gifts.tier2) : '',
        tier >= 3 ? this._selectorHTML(3, this._config.giftProducts.tier3, gifts.tier3) : '',
      ].join('');

      this.innerHTML = `
        <div class="cr-wrap">
          ${progressHTML}
          ${messageHTML}
          ${rewardsHTML}
          ${selectorHTML ? `<div class="cr-selectors">${selectorHTML}</div>` : ''}
        </div>`;

      // Attach gift-selection click handlers
      this.querySelectorAll('.cr-gift-option').forEach(btn => {
        btn.addEventListener('click', e => {
          if (this._busy) return;
          const variantId = e.currentTarget.dataset.variantId;
          const tierNum   = parseInt(e.currentTarget.dataset.tier, 10);

          // Optimistic UI: highlight selection immediately
          this.querySelectorAll(`.cr-gift-option[data-tier="${tierNum}"]`).forEach(b => {
            b.classList.remove('cr-gift-option--selected');
            b.setAttribute('aria-pressed', 'false');
          });
          e.currentTarget.classList.add('cr-gift-option--selected');
          e.currentTarget.setAttribute('aria-pressed', 'true');

          this._addGift(variantId, tierNum);
        });
      });
    }

    _selectorHTML(tierNum, options, selected) {
      const selectedId = selected ? String(selected.variantId) : null;
      const heading    = tierNum === 2
        ? 'Choose your free gift (included with your order):'
        : 'Choose your free bonus (included with your order):';

      const optionsHTML = options.map(opt => {
        const isSelected = String(opt.variantId) === selectedId;
        return `
          <button class="cr-gift-option ${isSelected ? 'cr-gift-option--selected' : ''}"
                  data-variant-id="${opt.variantId}"
                  data-tier="${tierNum}"
                  aria-pressed="${isSelected}"
                  type="button">
            <span class="cr-gift-option__label">${opt.label}</span>
            <span class="cr-gift-option__check" aria-hidden="true">${isSelected ? '✓' : ''}</span>
          </button>`;
      }).join('');

      return `
        <div class="cr-selector" data-selector-tier="${tierNum}">
          <p class="cr-selector__heading">${heading}</p>
          <div class="cr-selector__options" role="group" aria-label="Gift options for tier ${tierNum}">
            ${optionsHTML}
          </div>
        </div>`;
    }
  }

  customElements.define('cart-rewards', CartRewards);

  // ──────────────────────────────────────────────────────────────
  // Patch: re-render rewards whenever the cart drawer reloads its
  // content (e.g. after the user changes a quantity in cart-form).
  // ──────────────────────────────────────────────────────────────

  (function patchCartModal() {
    const tryPatch = () => {
      const modal = document.querySelector('cart-modal');
      if (!modal) return;

      const originalReload = modal.reloadContent?.bind(modal);
      if (!originalReload || modal._rewardPatched) return;
      modal._rewardPatched = true;

      modal.reloadContent = async function (...args) {
        const result = await originalReload(...args);
        document.querySelectorAll('cart-rewards').forEach(el => el.refresh());
        return result;
      };
    };

    // Try immediately and also once the DOM settles
    tryPatch();
    window.addEventListener('DOMContentLoaded', tryPatch, { once: true });
  })();

})();
